import {
    createRequestFilters,
    fetchAllPagesWithRateLimit,
    FilterMethod,
    flattenRequestFilters,
    ICustomFilter,
    ListProviderMessagesOrderEnum,
    ListProviderMessagesRequest,
    MeInfoResponse,
    MessageCategory,
    MessageLinkEntityType,
    MessagePurposeType,
    ProviderCoding,
    ProviderComment,
    ProviderDocument,
    ProviderDocumentUrl,
    ProviderMessage,
    ProviderMessageSortType,
    ProviderUser,
    runFireAndForget,
    serializeCustomFilterValue,
} from '@enter/core';
import _ from 'lodash';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import type { MessageGenerator } from './message.generator';
import { MessageService } from './message.service';
import { IFeatures, RootStore } from './root.store';
import { appendTo, equalsFilter, parseItemListingRequest, replaceInArray } from './util';

export interface IMessageListingRequest {
    page?: number;
    pageSize?: number;
    sort?: ProviderMessageSortType[];
    order?: ListProviderMessagesOrderEnum[];
    filters?: ICustomFilter[];
}

export interface IMessageListing {
    messages: ProviderMessage[];
    total: number;
}

function rawFilterValue(filter: ICustomFilter | undefined): string | undefined {
    return serializeCustomFilterValue(filter?.value);
}

/** Stable key for filters excluding filterRead — used to reuse all*FilteredTotal. */
function baseFiltersKey(filters: ICustomFilter[] | undefined): string {
    return (filters || [])
        .filter(f => f.filterId !== 'filterRead')
        .map(f => `${f.filterId}:${f.filterMethod}:${rawFilterValue(f) ?? ''}`)
        .sort()
        .join('|');
}

export interface IMessageDetail {
    message?: ProviderMessage;
    comments?: ProviderComment[];
    documents?: ProviderDocument[];
}

export interface IMessageCompose {
    subject: string;
    body: string;
    claimIds?: string[];
    files?: File[];
    priority?: string;
    dueDate?: string;
    categoryIds?: string[];
    assignedUserIds?: string[];
    purposeType?: MessagePurposeType;
}

export interface ICommentCompose {
    messageId: string;
    body: string;
    files?: File[];
}

export interface ICommentAttachment {
    document: ProviderDocument;
    documentUrl: ProviderDocumentUrl;
}

export interface IMessageFilter {
    open?: boolean;
    read?: boolean;
    claimId?: string;
}

export class MessageStore {
    @observable public unreadInboxTotal: number = 0;
    /** Server-derived unread count for Inbox, respecting currently active filters (not just loaded rows). */
    @observable public unreadInboxFilteredTotal: number = 0;
    /** Server-derived unread count for Done tab, respecting currently active filters. */
    @observable public unreadDoneFilteredTotal: number = 0;
    /** Server-derived total count for Inbox ignoring the read filter (all read states). */
    @observable public allInboxFilteredTotal: number = 0;
    /** Server-derived total count for Done tab ignoring the read filter (all read states). */
    @observable public allDoneFilteredTotal: number = 0;
    @observable public doneTotal: number = 0;
    @observable public doneMessages: ProviderMessage[] = [];
    @observable public inboxTotal: number = 0;
    @observable public inboxMessages: ProviderMessage[] = [];
    @observable public comments: ProviderComment[] = [];
    @observable public documents: ProviderDocument[] = [];
    @observable public documentUrls: ProviderDocumentUrl[] = [];
    @observable public messageCategories: MessageCategory[] = [];
    @observable public providerUsers: ProviderUser[] = [];

    // mapping messageId to array of commentIds
    @observable public commentIndex: Map<string, string[]> = new Map<string, string[]>();

    private features: IFeatures;
    private messageGenerator: MessageGenerator;
    private messageService: MessageService = new MessageService();
    private lastListing: IMessageListingRequest | undefined;
    private documentFiles = new Map<string, ProviderDocumentUrl>();
    private rootStore: RootStore;
    /** Tracks read state set by markAsRead/markAsUnread so we don't mutate list until updateMessageInList */
    @observable public lastKnownReadByMessageId: Record<string, boolean> = {};
    /** Identifies the most recent `loadMessagesForClaim` request, so a slower earlier one cannot apply. */
    private claimMessagesRequest: number = 0;
    /** Base-filter fingerprint for the last listing-derived allInboxFilteredTotal (skips duplicate all count). */
    private allInboxFilteredTotalBaseKey: string = '';
    /** Base-filter fingerprint for the last listing-derived allDoneFilteredTotal (skips duplicate all count). */
    private allDoneFilteredTotalBaseKey: string = '';

    constructor(features: IFeatures, messageGenerator: MessageGenerator, rootStore: RootStore) {
        this.features = features;
        this.messageGenerator = messageGenerator;
        this.rootStore = rootStore;
        // MobX 6+ requires makeObservable for decorators to work - without it MessageList won't re-render
        makeObservable(this);
    }

    public getUnreadTotal = async (): Promise<void> => {
        const req = {
            limit: 1,
            filterOpen: 'EQ:1',
            filterRead: 'EQ:0',
        };
        const count = await (this.features.isDemo
            ? this.messageGenerator.loadMessageCount(req)
            : this.messageService.fetchMessageCount(req));
        runInAction(() => {
            this.unreadInboxTotal = count;
        });
    };

    /**
     * Refresh unread inbox count from server. Call after markAsRead/markAsUnread
     * so the tab label and filter dropdown count stay in sync.
     */
    public refreshUnreadInboxCount = (): void => {
        runFireAndForget(() => this.getUnreadTotal(), 'refreshUnreadInboxCount');
    };

    /**
     * Fetch the unread count for the given listing request, respecting its active filters
     * but ignoring any filterRead selection. When a readFilter IS active, also fetches the
     * all-without-readfilter count (since the listing total is then a filtered subset).
     * When no readFilter is active, loadMessageListing already provides the all-total via
     * allInboxFilteredTotal/allDoneFilteredTotal, so no extra API call is needed.
     */
    public refreshReadFilterCounts = async (
        mlr: IMessageListingRequest,
        isDone: boolean,
        listingTotal?: number,
    ): Promise<void> => {
        const baseFilters = (mlr.filters || []).filter(f => f.filterId !== 'filterRead');
        const hasReadFilter = (mlr.filters || []).some(f => f.filterId === 'filterRead');
        const readRaw = rawFilterValue((mlr.filters || []).find(f => f.filterId === 'filterRead'));
        const baseKey = baseFiltersKey(mlr.filters);

        const unreadReq: IMessageListingRequest = {
            ...mlr,
            page: 0,
            pageSize: 1,
            filters: [
                ...baseFilters,
                { filterId: 'filterRead', filterMethod: FilterMethod.EQ, filterType: 'String', value: '0' },
            ],
        };

        const fetchCount = async (req: IMessageListingRequest): Promise<number> =>
            this.features.isDemo
                ? this.messageGenerator.loadMessageCount(mlr2lpmr(req))
                : this.messageService.fetchMessageCount(mlr2lpmr(req));

        if (hasReadFilter) {
            const canReuseUnread =
                readRaw === '0' && typeof listingTotal === 'number' && Number.isFinite(listingTotal);
            const canReuseAll = isDone
                ? this.allDoneFilteredTotalBaseKey === baseKey
                : this.allInboxFilteredTotalBaseKey === baseKey;
            const [unreadCount, allCount] = await Promise.all([
                canReuseUnread ? Promise.resolve(listingTotal!) : fetchCount(unreadReq),
                canReuseAll
                    ? Promise.resolve(isDone ? this.allDoneFilteredTotal : this.allInboxFilteredTotal)
                    : fetchCount({ ...mlr, page: 0, pageSize: 1, filters: baseFilters }),
            ]);
            runInAction(() => {
                if (isDone) {
                    this.unreadDoneFilteredTotal = unreadCount;
                    this.allDoneFilteredTotal = allCount;
                    if (!canReuseAll) {
                        this.allDoneFilteredTotalBaseKey = baseKey;
                    }
                } else {
                    this.unreadInboxFilteredTotal = unreadCount;
                    this.allInboxFilteredTotal = allCount;
                    if (!canReuseAll) {
                        this.allInboxFilteredTotalBaseKey = baseKey;
                    }
                }
            });
        } else {
            const unreadCount = await fetchCount(unreadReq);
            runInAction(() => {
                if (isDone) {
                    this.unreadDoneFilteredTotal = unreadCount;
                } else {
                    this.unreadInboxFilteredTotal = unreadCount;
                }
            });
        }
    };

    /** Unread count for Done tab (derived from doneMessages + lastKnownReadByMessageId). */
    @computed
    public get unreadDoneCount(): number {
        return this.doneMessages.filter(m => !this.isMessageRead(m)).length;
    }

    /** Unread count for Inbox tab (derived from inboxMessages + lastKnownReadByMessageId). */
    @computed
    public get unreadInboxFilteredCount(): number {
        return this.inboxMessages.filter(m => !this.isMessageRead(m)).length;
    }

    private isMessageRead(message: ProviderMessage): boolean {
        if (!message.id) return message.read;
        const overridden = this.lastKnownReadByMessageId[message.id];
        return overridden !== undefined ? overridden : message.read;
    }

    /**
     * Replace the inbox/done buckets with one claim's messages.
     *
     * The buckets are shared and hold no claim of their own, and consumers read them straight —
     * `components/messaging/messaging.tsx` renders `inboxMessages` without filtering by claim. So a
     * response that arrives after a newer claim was requested must not be applied: it would leave the
     * panel listing the messages of a claim the user has already navigated away from.
     */
    public async loadMessagesForClaim(claimId: string): Promise<void> {
        if (!this.features.isDemo) {
            const request = (this.claimMessagesRequest += 1);
            const messages = await this.messageService.fetchMessages({
                filterClaimId: equalsFilter(claimId),
            });
            if (request !== this.claimMessagesRequest) {
                return;
            }
            runInAction(() => {
                // Properly separate messages by their open status
                if (messages) {
                    const inboxMsgs = _.filter(messages, { open: true });
                    const doneMsgs = _.filter(messages, { open: false });

                    this.inboxMessages = inboxMsgs;
                    this.doneMessages = doneMsgs;
                    this.inboxTotal = inboxMsgs.length;
                    this.doneTotal = doneMsgs.length;
                } else {
                    this.inboxMessages = [];
                    this.doneMessages = [];
                    this.inboxTotal = 0;
                    this.doneTotal = 0;
                }
            });
        }
    }

    public async loadMessageListing(mlr: IMessageListingRequest, forceFetch?: boolean): Promise<IMessageListing> {
        const messageListing = await this.fetchMessageListing(mlr, forceFetch);
        this.applyMessageListing(mlr, messageListing);
        return messageListing;
    }

    /** Apply a previously fetched listing to the active inbox/done bucket. */
    public applyMessageListing(mlr: IMessageListingRequest, messageListing: IMessageListing): void {
        const lpmr = mlr2lpmr(mlr);
        const { messages, total } = messageListing;

        runInAction(() => {
            // filterOpen 'EQ:1' = inbox (open), 'EQ:0' = done (closed)
            // When filterOpen is missing, default to inbox (common for initial load)
            const isFilterOpen = _.matchesProperty('filterOpen', 'EQ:1');
            const isFilterDone = _.matchesProperty('filterOpen', 'EQ:0');
            const targetInbox = isFilterOpen(lpmr) || (!isFilterDone(lpmr) && !lpmr.filterOpen);
            // When no readFilter is active the listing total represents all messages —
            // piggyback on it to keep dropdown "all" counts accurate without an extra API call.
            const hasReadFilter = (mlr.filters || []).some(f => f.filterId === 'filterRead');
            // Always replace the active bucket with a new array (including []). Slice so
            // React memos that key on array identity see an empty listing as a change.
            const nextMessages = Array.isArray(messages) ? messages.slice() : [];
            const nextTotal = total ?? 0;
            if (targetInbox) {
                this.inboxMessages = nextMessages;
                this.inboxTotal = nextTotal;
                if (!hasReadFilter) {
                    this.allInboxFilteredTotal = nextTotal;
                    this.allInboxFilteredTotalBaseKey = baseFiltersKey(mlr.filters);
                }
            } else {
                this.doneMessages = nextMessages;
                this.doneTotal = nextTotal;
                if (!hasReadFilter) {
                    this.allDoneFilteredTotal = nextTotal;
                    this.allDoneFilteredTotalBaseKey = baseFiltersKey(mlr.filters);
                }
            }
            this.lastListing = mlr;
        });
    }

    /**
     * Stream every message for CSV export without mutating inbox/done observables.
     * Omits filterOpen so both inbox and done are returned in one listing.
     * Uses limit=100, 5 concurrent requests (sliding window), ≤100 req/min.
     */
    public async fetchAllMessagesForExport(
        mlr: Omit<IMessageListingRequest, 'page' | 'pageSize'>,
        options: {
            onProgress?: (loaded: number, total: number) => void;
            signal?: AbortSignal;
            /** Called immediately before each listing request. */
            beforeFetchPage?: (page: number) => void | Promise<void>;
            onPage: (
                items: ProviderMessage[],
                info: { page: number; total: number; loaded: number },
            ) => void | Promise<void>;
        },
    ): Promise<void> {
        const filters = (mlr.filters || []).filter(f => f.filterId !== 'filterOpen');

        await fetchAllPagesWithRateLimit<ProviderMessage>({
            pageSize: 100,
            concurrency: 5,
            maxRequestsPerMinute: 100,
            retainItems: false,
            onProgress: options.onProgress,
            signal: options.signal,
            beforeFetchPage: options.beforeFetchPage,
            onPage: options.onPage,
            getId: m => m.id,
            fetchPage: async (page, pageSize) => {
                const listing = await this.fetchMessageListing({
                    ...mlr,
                    page,
                    pageSize,
                    filters,
                });
                return { items: listing.messages, total: listing.total };
            },
        });
    }

    public async fetchMessageListing(mlr: IMessageListingRequest, forceFetch?: boolean): Promise<IMessageListing> {
        const lpmr = mlr2lpmr(mlr);
        const messageListing = await (this.features.isDemo
            ? this.messageGenerator.loadMessageListing(lpmr)
            : this.messageService.fetchMessageListing(lpmr));
        if (!messageListing) {
            return { messages: [], total: 0 };
        }
        return {
            messages: Array.isArray(messageListing.messages) ? messageListing.messages : [],
            total: messageListing.total ?? 0,
        };
    }

    public async searchCommentsByBody(args: {
        filterBody: string[];
        limit: number;
        offset: number;
    }): Promise<ProviderComment[]> {
        if (this.features.isDemo) {
            return this.messageGenerator.searchCommentsByBody(args);
        }
        return this.messageService.searchCommentsByBody(args);
    }

    /**
     * Demo has no flat coding collection to search, so there is no truthful answer to give here.
     * The Coding result field is hidden in demo instead of being answered with an empty list,
     * which on screen is indistinguishable from "nothing matches" (NS-4) — see the provider
     * Tasks page, which passes `showCodingSearch={!rootStore.isDemo()}`.
     */
    public async searchCodingsByResult(args: {
        filterResult: string[];
        limit: number;
        offset: number;
    }): Promise<ProviderCoding[]> {
        if (this.features.isDemo) {
            return [];
        }
        return this.messageService.searchCodingsByResult(args);
    }

    /**
     * The codings a demo task links to, for `CodingReasonCards` through `onCodingsResolve` (FE-81).
     *
     * Demo mode only — master's Tasks page passes this only then. The card fetches a real coding
     * through the API client, which the demo boundary refuses, so a halted demo task would show
     * no rules. Kept beside `searchCodingsByResult` so that merge does not drop it.
     *
     * This branch predates FE-81's `MessageGenerator.resolveCodings`; call it when present.
     */
    public resolveDemoCodings = (codingIds: string[]): Promise<ProviderCoding[]> => {
        const resolve = (
            this.messageGenerator as MessageGenerator & {
                resolveCodings?: (ids: string[]) => Promise<ProviderCoding[]>;
            }
        ).resolveCodings;
        return resolve ? resolve(codingIds) : Promise.resolve([]);
    };

    /** Count messages for arbitrary filters without mutating list observables. */
    public async countMessages(mlr: IMessageListingRequest): Promise<number> {
        const listing = await this.fetchMessageListing({ ...mlr, page: 0, pageSize: 1 });
        return listing.total;
    }

    public async loadMessage(messageId: string, forceFetch?: boolean): Promise<ProviderMessage | undefined> {
        const gpmr = {
            id: messageId,
        };
        const message = await (this.features.isDemo
            ? this.messageGenerator.loadMessage(gpmr)
            : this.messageService.fetchMessage(gpmr));
        if (!message) {
            throw new Error(`No messages available`);
        }
        return message;
    }

    public async loadMessageDetail(messageId: string, forceFetch?: boolean): Promise<IMessageDetail> {
        // When not forcing, reuse a list-row message to skip a duplicate GET; comments still load below.
        const existing = forceFetch ? undefined : this.findMessage(messageId);
        const message = existing ?? (await this.loadMessage(messageId));
        if (!message) {
            throw new Error(`No message for ID: ${messageId}`);
        }

        const messageDetail = await (this.features.isDemo
            ? this.messageGenerator.loadMessageDetail(messageId)
            : this.messageService.fetchMessageDetail(message, !!existing && !forceFetch));
        if (!messageDetail) {
            throw new Error(`No message detail for ID: ${messageId}`);
        }
        const { comments, documents } = messageDetail;
        runInAction(() => {
            // Do NOT modify inboxMessages/doneMessages order here.
            // The caller (e.g., refreshMessageDetail) will sync the updated message
            // back to the lists via updateMessageInList to preserve ordering.
            this.comments = appendTo<ProviderComment>(this.comments, comments);
            this.documents = appendTo<ProviderDocument>(this.documents, documents);
            this.updateIndex(this.commentIndex, messageId, comments);
        });
        return messageDetail;
    }

    /** Drop detail-only caches so panel switches do not retain stale comments/documents. */
    @action
    public clearMessageDetailCache(): void {
        this.comments = [];
        this.documents = [];
        this.documentUrls = [];
        this.commentIndex = new Map<string, string[]>();
        this.documentFiles.clear();
    }

    @action
    public async loadDocumentUrl(documentId?: string): Promise<string | undefined> {
        let documentUrl = _.find(this.documentUrls, { documentId });
        if (documentUrl) {
            return documentUrl.url;
        }
        if (documentId) {
            documentUrl = await (this.features.isDemo
                ? this.messageGenerator.loadDocumentUrl(documentId)
                : this.messageService.fetchDocumentUrl(documentId));
            if (documentUrl) {
                runInAction(() => {
                    this.documentUrls = appendTo<ProviderDocumentUrl>(this.documentUrls, documentUrl, 'url');
                });
                return documentUrl.url;
            }
        }
    }
    @action
    public async loadDocument(id: string): Promise<ProviderDocument | undefined> {
        let document = _.find(this.documents, { id });

        if (document) {
            return document;
        }

        document = await (this.features.isDemo
            ? this.messageGenerator.loadDocument(id)
            : this.messageService.fetchDocument(id));

        if (document) {
            runInAction(() => {
                this.documents = appendTo<ProviderDocument>(this.documents, document, 'id');
            });

            return document;
        }
    }

    @action
    public async composeMessage(
        account: MeInfoResponse | undefined,
        subject: string,
        body: string,
        claimIds?: string[],
        files?: File[],
        priority?: string,
        dueDate?: string,
        categoryIds?: string[],
        assignedUserIds?: string[],
        isPrivateNote?: boolean,
    ): Promise<string | undefined> {
        const messageCompose = {
            subject,
            body: body || ' ',
            claimIds, // Pass all claim IDs for linking via /link endpoint
            files,
            priority,
            dueDate,
            categoryIds,
            assignedUserIds,
            purposeType: isPrivateNote ? MessagePurposeType.PROVIDEROWNED : undefined,
        };
        const composed = await (this.features.isDemo
            ? this.messageGenerator.composeMessage(messageCompose, account)
            : this.messageService.composeMessage(messageCompose));

        if (this.lastListing) {
            await this.loadMessageListing({
                ...this.lastListing,
                page: 0,
                filters: [
                    {
                        filterId: 'filterOpen',
                        filterMethod: FilterMethod.EQ,
                        filterType: 'String',
                        value: '1',
                    },
                    // ...(this.lastListing.filters || []),
                ],
            });
        }

        return composed?.message?.id;
    }

    @action
    public async addComment(
        account: MeInfoResponse | undefined,
        messageId: string,
        body: string,
        files?: File[],
    ): Promise<void> {
        let message = _.find(_.concat([], this.doneMessages, this.inboxMessages), { id: messageId });

        // If message not found in store, try to load it
        if (!message) {
            try {
                const messageDetail = await this.loadMessageDetail(messageId);
                message = messageDetail.message;

                if (message) {
                    runInAction(() => {
                        const wasInStore = !!_.find(_.concat([], this.doneMessages, this.inboxMessages), {
                            id: messageId,
                        });
                        if (message.open) {
                            this.inboxMessages = appendTo<ProviderMessage>(this.inboxMessages, message);
                            if (!wasInStore) {
                                this.inboxTotal += 1;
                            }
                        } else {
                            this.doneMessages = appendTo<ProviderMessage>(this.doneMessages, message);
                            if (!wasInStore) {
                                this.doneTotal += 1;
                            }
                        }
                    });
                }
            } catch (error) {
                throw error;
            }
        }

        if (!message) {
            throw new Error(`Unable to load message with id '${messageId}' for adding a comment.`);
        }

        const commentCompose = {
            messageId,
            body,
            files,
        };
        const comment = await (this.features.isDemo
            ? this.messageGenerator.composeComment(commentCompose, account)
            : this.messageService.composeComment(commentCompose));
        runInAction(() => {
            this.comments = appendTo<ProviderComment>(this.comments, comment);
            const indexKey = message && message.id ? message.id : messageId;
            // this.updateIndex(this.commentIndex, indexKey, [comment]);
        });
    }

    @action
    public async markAsOpen(id?: string): Promise<void> {
        const message = this.findMessage(id);
        if (id && message && !message.open) {
            await (this.features.isDemo ? this.messageGenerator.markAsOpen(id) : this.messageService.markAsOpen(id));
            runInAction(() => {
                // message was closed (Done), now mark open (Inbox)
                message.open = true;
                // remove from Done - use filter to create new array reference
                this.doneMessages = _.filter(this.doneMessages, m => m.id !== id);
                this.doneTotal -= 1;
                // add to Inbox
                this.inboxMessages = _.concat(this.inboxMessages, message);
                this.inboxTotal += 1;
            });
        }
    }

    @action
    public async markAsClosed(id?: string): Promise<void> {
        const message = this.findMessage(id);
        if (id && message && message.open) {
            await (this.features.isDemo
                ? this.messageGenerator.markAsClosed(id)
                : this.messageService.markAsClosed(id));
            runInAction(() => {
                // message was open (Inbox), now mark closed (Done)
                message.open = false;
                // remove from Inbox - use filter to create new array reference
                this.inboxMessages = _.filter(this.inboxMessages, m => m.id !== id);
                this.inboxTotal -= 1;
                // add to Done
                this.doneMessages = _.concat(this.doneMessages, message);
                this.doneTotal += 1;
            });
        }
    }

    /**
     * Close many messages with ONE store mutation.
     *
     * There is no bulk close endpoint, so this still sends one request per id — but it applies the
     * result once, at the end. Calling {@link markAsClosed} in a loop mutates `inboxMessages` per
     * response, and each mutation re-renders the whole task table: the virtualised row count drops
     * by one, which schedules a state update from a passive effect. Past 50 of those in a row React
     * throws "Maximum update depth exceeded" (error #185) — reproduced in support with 100 rows.
     *
     * Uses allSettled on purpose: one rejected id must not abandon the rest half-applied, and the
     * caller needs to know what actually closed so the list can be reconciled.
     */
    @action
    public async markManyAsClosed(ids: string[]): Promise<{ closed: string[]; failed: string[] }> {
        const closed: string[] = [];
        const failed: string[] = [];
        // Same precondition as the single-id path: only messages we hold and that are still open.
        const targets = _.filter(_.uniq(_.compact(ids)), id => !!this.findMessage(id)?.open);

        if (!targets.length) {
            return { closed, failed };
        }

        const results = await Promise.allSettled(
            _.map(targets, async (id): Promise<void> =>
                this.features.isDemo
                    ? this.messageGenerator.markAsClosed(id)
                    : this.messageService.markAsClosed(id),
            ),
        );
        results.forEach((result, index) => {
            (result.status === 'fulfilled' ? closed : failed).push(targets[index]);
        });

        if (closed.length) {
            // Key the removal off the messages we actually hold, so the list and the total can
            // never drift apart — same rule the single-id path follows.
            const moved = _.compact(_.map(closed, id => this.findMessage(id)));
            const movedIds = new Set(_.compact(_.map(moved, m => m.id)));

            runInAction(() => {
                moved.forEach(message => {
                    message.open = false;
                });

                this.inboxMessages = _.filter(this.inboxMessages, m => !m.id || !movedIds.has(m.id));
                this.inboxTotal -= moved.length;

                this.doneMessages = _.concat(this.doneMessages, moved);
                this.doneTotal += moved.length;
            });
        }

        return { closed, failed };
    }

    /**
     * Reopen many messages with ONE store mutation — the Done-tab twin of
     * {@link markManyAsClosed}, and prone to the same React #185 storm when looped per id.
     */
    @action
    public async markManyAsOpen(ids: string[]): Promise<{ opened: string[]; failed: string[] }> {
        const opened: string[] = [];
        const failed: string[] = [];
        // Same precondition as the single-id path: only messages we hold and that are closed.
        const targets = _.filter(_.uniq(_.compact(ids)), id => {
            const message = this.findMessage(id);
            return !!message && !message.open;
        });

        if (!targets.length) {
            return { opened, failed };
        }

        const results = await Promise.allSettled(
            _.map(targets, async (id): Promise<void> =>
                this.features.isDemo
                    ? this.messageGenerator.markAsOpen(id)
                    : this.messageService.markAsOpen(id),
            ),
        );
        results.forEach((result, index) => {
            (result.status === 'fulfilled' ? opened : failed).push(targets[index]);
        });

        if (opened.length) {
            const moved = _.compact(_.map(opened, id => this.findMessage(id)));
            const movedIds = new Set(_.compact(_.map(moved, m => m.id)));

            runInAction(() => {
                moved.forEach(message => {
                    message.open = true;
                });

                this.doneMessages = _.filter(this.doneMessages, m => !m.id || !movedIds.has(m.id));
                this.doneTotal -= moved.length;

                this.inboxMessages = _.concat(this.inboxMessages, moved);
                this.inboxTotal += moved.length;
            });
        }

        return { opened, failed };
    }

    @action
    public async markAsRead(id?: string): Promise<void> {
        const message = this.findMessage(id);
        const { roleId } = this.rootStore;
        const isReadOnly: boolean = roleId === 'PROVIDER_READ_ACCESS';
        const effectiveRead = id ? (this.lastKnownReadByMessageId[id] ?? message?.read) : true;
        if (id && message && !effectiveRead && !isReadOnly) {
            await (this.features.isDemo ? this.messageGenerator.markAsRead(id) : this.messageService.markAsRead(id));
            runInAction(() => {
                this.lastKnownReadByMessageId[id] = true;
                if (message.open) {
                    this.unreadInboxTotal = Math.max(0, this.unreadInboxTotal - 1);
                    this.unreadInboxFilteredTotal = Math.max(0, this.unreadInboxFilteredTotal - 1);
                } else {
                    this.unreadDoneFilteredTotal = Math.max(0, this.unreadDoneFilteredTotal - 1);
                }
            });
            // Backend sync to ensure counts stay correct even if local math drifts.
            this.refreshUnreadInboxCount();
        }
    }

    @action
    public async markAsUnread(id?: string): Promise<void> {
        const message = this.findMessage(id);
        const { roleId } = this.rootStore;
        const isReadOnly: boolean = roleId === 'PROVIDER_READ_ACCESS';
        const effectiveRead = id ? (this.lastKnownReadByMessageId[id] ?? message?.read) : false;
        if (id && message && effectiveRead && !isReadOnly) {
            await (this.features.isDemo
                ? this.messageGenerator.markAsUnread(id)
                : this.messageService.markAsUnread(id));
            runInAction(() => {
                this.lastKnownReadByMessageId[id] = false;
                if (message.open) {
                    this.unreadInboxTotal += 1;
                    this.unreadInboxFilteredTotal += 1;
                } else {
                    this.unreadDoneFilteredTotal += 1;
                }
            });
            // Backend sync to ensure counts stay correct even if local math drifts.
            this.refreshUnreadInboxCount();
        }
    }

    public findMessage = (messageId: string | undefined): ProviderMessage | undefined => {
        const p = { id: messageId };
        const i = _.find<ProviderMessage>(this.inboxMessages, p);
        if (i) {
            return i;
        }
        const d = _.find<ProviderMessage>(this.doneMessages, p);
        if (d) {
            return d;
        }
    };
    public findMessages = (filter: IMessageFilter): ProviderMessage[] => {
        const p = { ...filter };
        const i = _.filter<ProviderMessage>(this.inboxMessages, p);
        const d = _.filter<ProviderMessage>(this.doneMessages, p);
        return _.concat<ProviderMessage>([], i, d);
    };

    /**
     * Update a message in the inbox/done lists with new data.
     * This syncs changes made in the detail panel back to the table.
     */
    @action
    public updateMessageInList = (updatedMessage: ProviderMessage): void => {
        if (!updatedMessage.id) return;

        this.lastKnownReadByMessageId[updatedMessage.id] = updatedMessage.read;

        // Try replace in inbox first
        const nextInbox = replaceInArray(this.inboxMessages, updatedMessage, m => m.id as string);
        if (nextInbox !== this.inboxMessages) {
            this.inboxMessages = nextInbox;
            return;
        }

        // Then try replace in done
        const nextDone = replaceInArray(this.doneMessages, updatedMessage, m => m.id as string);
        if (nextDone !== this.doneMessages) {
            this.doneMessages = nextDone;
        }
    };

    /**
     * Update message priority/dueDate and sync to list.
     */
    @action
    public async updateMessage(
        id: string,
        payload: { priority?: string; dueDate?: string | null },
        skipReload?: boolean,
    ): Promise<void> {
        await (this.features.isDemo
            ? Promise.resolve(this.messageGenerator.updateMessage(id, payload))
            : this.messageService.updateMessage(id, payload));

        // Skip reload if requested (e.g., when caller will refresh separately)
        if (skipReload) {
            return;
        }

        // Fetch fresh message and sync to list
        const message = await this.loadMessage(id);
        if (message) {
            this.updateMessageInList(message);
        }
    }

    /**
     * Link entities (claims, categories, users) to a message.
     */
    @action
    public async linkEntityToMessage(
        id: string,
        entityType: MessageLinkEntityType,
        entityIds: string[],
        skipReload?: boolean,
    ): Promise<void> {
        await (this.features.isDemo
            ? Promise.resolve(this.messageGenerator.linkEntityToMessage(id, entityType, entityIds))
            : this.messageService.linkEntityToMessage(id, entityType, entityIds));

        if (skipReload) {
            return;
        }

        const message = await this.loadMessage(id);
        if (message) {
            this.updateMessageInList(message);
        }
    }

    /**
     * Unlink entities (claims, categories, users) from a message.
     */
    @action
    public async unlinkEntityFromMessage(
        id: string,
        entityType: MessageLinkEntityType,
        entityIds: string[],
        skipReload?: boolean,
    ): Promise<void> {
        await (this.features.isDemo
            ? Promise.resolve(this.messageGenerator.unlinkEntityFromMessage(id, entityType, entityIds))
            : this.messageService.unlinkEntityFromMessage(id, entityType, entityIds));

        if (skipReload) {
            return;
        }

        const message = await this.loadMessage(id);
        if (message) {
            this.updateMessageInList(message);
        }
    }

    public findCommentByMessageId = (messageId: string): ProviderComment | undefined => {
        return _.first(this.findCommentsByMessageId(messageId));
    };
    public findCommentsByMessageId = (messageId: string): ProviderComment[] => {
        const commentIds = this.commentIndex.get(messageId);
        return _.compact(_.map(commentIds, commentId => _.find(this.comments, { id: commentId })));
    };

    public findAttachments = (documentIds: string[]): ICommentAttachment[] => {
        return _.compact(
            _.map(documentIds, id => {
                const document = _.find(this.documents, { id });
                const documentUrl = _.find(this.documentUrls, { documentId: id });
                if (document && documentUrl) {
                    return {
                        document,
                        documentUrl,
                    };
                }
            }),
        );
    };

    private updateIndex(map: Map<string, string[]>, key: string, values?: { id?: string }[]): void {
        if (values) {
            const newValues: string[] = _.compact(_.map(values, 'id'));
            const oldValues: string[] = map.get(key) || [];
            map.set(key, _.uniq(_.concat(oldValues, newValues)));
        }
    }

    public embed = async (id: string): Promise<ProviderDocumentUrl> => {
        const cachedDocument = this.documentFiles.get(id);
        if (cachedDocument) {
            return cachedDocument;
        }
        const url = await (this.features.isDemo ? this.messageGenerator.embed(id) : this.messageService.embed(id));

        // cache files
        this.documentFiles.set(id, url);

        return url;
    };

    public download = async (id: string): Promise<ProviderDocumentUrl> => {
        return await (this.features.isDemo ? this.messageGenerator.download(id) : this.messageService.download(id));
    };

    @action
    public async loadMessageCategories(): Promise<void> {
        if (this.messageCategories.length > 0) {
            return;
        }
        // Demo mode used to fall out of this method entirely, leaving the list empty — so
        // `getCategoryName` resolved every id to undefined and the Tasks page could not group a
        // single task, however well the generator categorised them. The generator owns the system
        // categories; this just serves them (NS-14: generated, never fetched).
        if (this.features.isDemo) {
            const categories = this.messageGenerator.messageCategories;
            runInAction(() => {
                this.messageCategories = categories || [];
            });
            return;
        }
        try {
            const categories = await this.messageService.fetchMessageCategories();
            runInAction(() => {
                this.messageCategories = categories || [];
            });
        } catch (error) {
            console.error('Failed to load message categories', error);
            runInAction(() => {
                this.messageCategories = [];
            });
        }
    }

    public getCategoryName = (categoryId: string): string | undefined => {
        if (!categoryId) {
            return undefined;
        }
        const category = _.find(this.messageCategories, { id: categoryId });
        return category?.name;
    };

    @action
    public async loadProviderUsers(): Promise<void> {
        if (!this.features.isDemo && this.providerUsers.length === 0) {
            try {
                const users = await this.messageService.fetchProviderUsers();
                runInAction(() => {
                    this.providerUsers = users || [];
                });
            } catch (error) {
                runInAction(() => {
                    this.providerUsers = [];
                });
            }
        }
    }

    public getUserName = (userId: string): string | undefined => {
        if (!userId) {
            return undefined;
        }
        const user = _.find(this.providerUsers, u => u.userId === userId || u.personalAccountId === userId);
        if (user) {
            const fullName = _.compact([user.first, user.last]).join(' ');
            return fullName || undefined;
        }
        return undefined;
    };
}

// Convert from IMessageListingRequest to ListProviderMessagesRequest
export const mlr2lpmr = (ilr: IMessageListingRequest): ListProviderMessagesRequest => {
    const base = parseItemListingRequest(ilr) as ListProviderMessagesRequest;
    const filters = createRequestFilters(ilr.filters, true);

    // NB: for ListProviderMessagesRequest, filterRead and filterOpen are string, not string[]
    flattenRequestFilters(filters, ['filterOpen', 'filterRead']);

    return {
        ...base,
        ...filters,
    };
};
